import { formatAmount } from './misc';

/**
 * The headline figures for one employee's pay, shown on the employee record.
 *
 * `employees.current_salary` is a MONTHLY gross figure — the column holds values from ₹3,700 to
 * ₹55,000. This used to read it as an annual salary and divide by twelve, which showed a senior
 * engineer earning ₹4,583 a month.
 *
 * The deductions mirror `PayrollService` on the API, which is what actually pays people: a flat
 * ₹200 professional tax and 10% TDS. Change them there and here together — this is an estimate
 * of a normal month, while the payslip is the record of a particular one.
 */

/** Matches `PayrollService.HOURS_PER_DAY`. */
const HOURS_PER_DAY = 8.5;

/** Mon-Sat, averaged over a year: 313 working days ÷ 12. */
const AVERAGE_WORKING_DAYS = 26;

const PROFESSIONAL_TAX = 200;
const TDS_RATE = 0.1;

export interface SalaryBreakdown {
  annual: number;
  monthly: number;
  tds: number;
  professionalFees: number;
  inHand: number;
  perHour: number;
}

export function calculateSalaryBreakdown(monthlySalary: number = 0): SalaryBreakdown {
  const monthly = Number(monthlySalary) || 0;
  const annual = monthly * 12;
  const tds = monthly > 0 ? monthly * TDS_RATE : 0;
  const professionalFees = monthly > 0 ? PROFESSIONAL_TAX : 0;
  const inHand = monthly - tds - professionalFees;
  const perHour = monthly > 0 ? monthly / (AVERAGE_WORKING_DAYS * HOURS_PER_DAY) : 0;
  return { annual, monthly, tds, professionalFees, inHand, perHour };
}

export interface SalaryRow {
  label: string;
  value: string;
}

export function getSalaryBreakdownRows(breakdown: SalaryBreakdown | null | undefined): SalaryRow[] {
  if (!breakdown) return [];
  return [
    { label: 'Salary per month', value: formatAmount(breakdown.monthly) },
    { label: 'Salary per annum', value: formatAmount(breakdown.annual) },
    { label: 'Professional Tax', value: `-${formatAmount(breakdown.professionalFees)}` },
    { label: 'TDS', value: `-${formatAmount(breakdown.tds)}` },
    { label: 'Salary in hand', value: formatAmount(breakdown.inHand) },
    { label: 'Salary per hour', value: formatAmount(breakdown.perHour) },
  ];
}
